import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Layers, ArrowRight, ArrowDown, CheckCircle2 } from "lucide-react";

const EBOOK_PURPOSES = [
  "Deep Understanding",
  "Real-World Application",
  "Structured Learning",
  "Cross-Career Relevance",
];

const WAIT_LINKS = [
  { label: "Playbooks", to: "/playbooks" },
  { label: "Career AI", to: "/career-ai" },
  { label: "Finance Interview Foundations Guide", href: "https://payhip.com/b/Eyemc" },
];

const CAREERS = ["Investment Banking", "Private Equity", "Energy Finance"];

export default function Ebooks() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-12" data-testid="ebooks-page">
      {/* 1 — Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl"
      >
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
          VantStride Ebooks · Coming Soon
        </div>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-[#0A2540] font-medium mt-3">
          Master one topic. Completely.
        </h1>
        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
          Playbooks prepare you for a career.
          <br className="hidden sm:block" />
          Ebooks go deep on one topic that powers many finance careers.
        </p>
      </motion.div>

      {/* 2 — Playbooks vs Ebooks */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mt-14 sm:mt-10 grid sm:grid-cols-2 gap-6"
        data-testid="ebooks-vs-playbooks"
      >
        <div className="rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            <Layers className="w-3.5 h-3.5" />
            Playbooks
          </div>
          <h2 className="font-display text-xl text-[#0A2540] mt-3">One career, end to end.</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Understand the role, build the skills, prepare for the work, and know where to go next.
          </p>
          <p className="mt-2 text-sm text-slate-600">IB · PE · Energy Finance</p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            <BookOpen className="w-3.5 h-3.5" />
            Ebooks
          </div>
          <h2 className="font-display text-xl text-[#0A2540] mt-3">One topic, in depth.</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Go beyond the career overview and build a deeper understanding of a specific finance topic that applies across careers.
          </p>
        </div>
      </motion.div>

      {/* 3 — What every ebook is built for */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="mt-14 sm:mt-10"
        data-testid="ebooks-purpose"
      >
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium">
          What every ebook is built for
        </h2>
        <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-slate-700">
          {EBOOK_PURPOSES.map((item) => (
            <li key={item} className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 vestryn-accent flex-shrink-0 mt-0.5" /> {item}
            </li>
          ))}
        </ul>
      </motion.div>

      {/* 4 — Career → Topic */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="mt-14 sm:mt-10 rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 sm:p-10"
        data-testid="ebooks-career-topic"
      >
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
          Career → Topic
        </div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium mt-3 max-w-2xl leading-tight">
          A Career Hub helps you understand where you're going.
        </h2>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
          An Ebook helps you understand one important part of the world you're entering.
        </p>
        <div className="mt-8 flex flex-col items-start gap-4">
          <div className="flex flex-wrap gap-2">
            {CAREERS.map((c) => (
              <span
                key={c}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
              >
                {c}
              </span>
            ))}
          </div>
          <ArrowDown className="w-4 h-4 vestryn-accent ml-6" aria-hidden="true" />
          <span className="rounded-full border border-[#C6A15B]/40 bg-[#FBF7EE] px-4 py-2 text-sm font-medium text-[#0A2540]">
            M&A
          </span>
        </div>
      </motion.div>

      {/* 5 — While you wait */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="mt-14 sm:mt-10 rounded-3xl bg-[#0A2540] text-white p-8 sm:p-12"
        data-testid="ebooks-while-you-wait"
      >
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
          While you wait.
        </h2>
        <p className="mt-3 text-slate-200 max-w-2xl leading-relaxed">
          Explore what's already available on VantStride while the first ebooks are being developed.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {WAIT_LINKS.map((l) => {
            const Tag = l.href ? "a" : Link;
            const linkProps = l.href
              ? { href: l.href, target: "_blank", rel: "noopener noreferrer" }
              : { to: l.to };
            return (
              <Tag
                key={l.label}
                {...linkProps}
                data-testid={`ebooks-wait-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                {l.label}
                <ArrowRight className="w-4 h-4" />
              </Tag>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
